import { FC, forwardRef } from 'react';
import { DialogProps } from '@toolpad/core/useDialogs';

import {
  Dialog,
  DialogContent,
  Zoom,
  Box,
  Grid,
  CircularProgress,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { ViewNavigationBar } from '@components';
import { useGetVenueDecorations, useCurrentVenueSlug } from '@customer/features/venues';
import { useGetSpacing } from '@ui';
import { DecorationListItem } from './DecorationListItem';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Zoom ref={ref} {...props} />;
});

export const DecorationSelectionDialog: FC<DialogProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const venueSlug = useCurrentVenueSlug();
  const getSpacing = useGetSpacing();
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data: decorations, isPending } = useGetVenueDecorations(venueSlug, {
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const handleClose = async () => {
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose()}
        fullWidth={true}
        maxWidth="lg"
        fullScreen={fullScreen}
        scroll="paper"
        slots={{
          transition: Transition,
        }}
        slotProps={{
          paper: {
            sx: {
              height: '100%',
            },
          },
        }}
      >
        <ViewNavigationBar
          onClose={() => onClose()}
          closeTitle={t('close')}
          title={t('selectYourDecoration')}
          onNext={handleClose}
          sx={{
            backgroundColor: '#ECF1FC',
          }}
        />
        <DialogContent
          sx={{
            position: 'relative',
            height: '100%',
            minHeight: {
              md: 'calc(100vh - 200px)',
            },
            padding: 8,
            paddingLeft: getSpacing('xl'),
            paddingRight: getSpacing('xl'),
            backgroundColor: '#ECF1FC',
          }}
        >
          {isPending ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={getSpacing('xl')}>
              {decorations &&
                decorations.map((decoration) => {
                  return (
                    <Grid
                      size={{ xs: 6, sm: 6, md: 2, lg: 2 }}
                      key={decoration.id}
                    >
                      <DecorationListItem
                        decoration={decoration}
                        onClose={handleClose}
                      />
                    </Grid>
                  );
                })}
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
